// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * Sign in with Apple on the web (PLAN.md P1-T02), through Apple's own sign-in page, like
 * Google: no Apple script runs on our pages. Asking for the name and email makes Apple answer
 * with a form POST (response_mode=form_post), which a single-page app cannot read, so Apple
 * posts to the API (`POST /v1/auth/oidc/apple/return`, registered on the Services ID), and the
 * API sends the person on to /auth/apple with the ID token, the authorization code, the state
 * and (first sign-in only) the name in the URL fragment. From there it is Google's flow: the
 * server checks the token (`POST /v1/auth/oidc/apple`), Apple gets hex(SHA-256(nonce)), the
 * server the raw nonce, and the state ties the return to this browser. Off when the build has
 * no Services ID, and in the desktop app (which signs in through the browser, P4-T08).
 */
import { newNonce, newState, safeNext, takeAttempt, type Attempt } from "./googleSignIn.js";
import { isDesktop } from "./platform.js";

/** The Services ID (md.granite.web in production). */
export const appleClientId = isDesktop ? undefined : import.meta.env.VITE_APPLE_CLIENT_ID;

const attemptKey = "granite.apple.attempt";

/** Where Apple posts its answer: the API, which forwards it to /auth/apple. */
export function appleRedirectUri(): string {
  return `${import.meta.env.VITE_API_URL ?? window.location.origin}/v1/auth/oidc/apple/return`;
}

/**
 * Sends the browser to Apple's sign-in page; next is where to land afterwards. With a
 * workspace name, a new account becomes a business account owning that workspace.
 */
export async function startAppleSignIn(next: string, workspaceName?: string): Promise<void> {
  if (!appleClientId) return;
  const nonce = await newNonce();
  const attempt: Attempt = {
    state: newState(),
    nonce: nonce.raw,
    next,
    ...(workspaceName ? { workspaceName } : {}),
  };
  sessionStorage.setItem(attemptKey, JSON.stringify(attempt));
  const url = new URL("https://appleid.apple.com/auth/authorize");
  url.search = new URLSearchParams({
    client_id: appleClientId,
    redirect_uri: appleRedirectUri(),
    response_type: "code id_token",
    response_mode: "form_post",
    scope: "name email",
    nonce: nonce.hashed,
    state: attempt.state,
  }).toString();
  window.location.assign(url.toString());
}

export type AppleReturn =
  | {
      kind: "token";
      idToken: string;
      nonce: string;
      /** The authorization code, which lets the server revoke Granite's access later. */
      code?: string;
      /** The person's name: Apple gives it only on the first sign-in. */
      name?: string;
      next: string;
      workspaceName?: string;
    }
  | { kind: "canceled" }
  | { kind: "invalid" };

/**
 * Reads what Apple (through the API) sent back in the fragment, once: the attempt is
 * forgotten, so a token cannot be replayed from this page.
 */
export function takeAppleReturn(fragment: string): AppleReturn {
  const params = new URLSearchParams(fragment.replace(/^#/, ""));
  const attempt = takeAttempt(attemptKey);
  if (params.get("error")) return { kind: "canceled" };
  const idToken = params.get("id_token");
  if (!attempt || !idToken || params.get("state") !== attempt.state) return { kind: "invalid" };
  const code = params.get("code");
  const name = params.get("name")?.trim();
  return {
    kind: "token",
    idToken,
    nonce: attempt.nonce,
    ...(code ? { code } : {}),
    ...(name ? { name } : {}),
    next: safeNext(attempt.next),
    ...(attempt.workspaceName ? { workspaceName: attempt.workspaceName } : {}),
  };
}
